import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { serviceAreasData, getServiceAreasByRegion } from '../data/serviceAreas';

const { FiMapPin, FiUsers, FiSearch, FiArrowRight, FiFilter } = FiIcons;

const ServiceAreas = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('All');
  
  const regionData = getServiceAreasByRegion();
  const regions = ['All', ...Object.keys(regionData)];
  
  const filteredAreas = serviceAreasData.filter(area => {
    const matchesSearch = area.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         area.region.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRegion = selectedRegion === 'All' || area.region === selectedRegion;
    return matchesSearch && matchesRegion;
  });

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-slate-900 via-slate-800 to-orange-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center text-white"
          >
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Service Areas Across the UK
            </h1>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto mb-8">
              From London to Edinburgh, we help businesses across the United Kingdom establish powerful online presences. 
              Discover how we've transformed local businesses in your area with professional web design and digital marketing.
            </p>
            
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-2xl mx-auto">
              <div className="text-center">
                <div className="text-3xl font-bold text-orange-400">12+</div>
                <div className="text-slate-300 text-sm">Major Cities</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-orange-400">50+</div>
                <div className="text-slate-300 text-sm">Local Clients</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-orange-400">100%</div>
                <div className="text-slate-300 text-sm">UK Coverage</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-orange-400">5★</div>
                <div className="text-slate-300 text-sm">Service Rating</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Search and Filter */}
      <section className="py-12 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row gap-6 items-center justify-between">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Search cities or regions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>
            
            {/* Region Filter */}
            <div className="flex items-center space-x-2">
              <SafeIcon icon={FiFilter} className="text-slate-600" />
              <select
                value={selectedRegion}
                onChange={(e) => setSelectedRegion(e.target.value)}
                className="px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white"
              >
                {regions.map(region => (
                  <option key={region} value={region}>{region}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </section>

      {/* Service Areas Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {filteredAreas.length === 0 ? (
            <div className="text-center py-16">
              <div className="text-slate-400 text-xl mb-4">No service areas found</div>
              <button 
                onClick={() => { setSearchTerm(''); setSelectedRegion('All'); }}
                className="text-orange-600 hover:text-orange-700 font-medium"
              >
                Clear filters
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredAreas.map((area, index) => (
                <motion.div
                  key={area.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <Link 
                    to={`/service-areas/${area.slug}`}
                    className="group block bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border border-slate-100 hover:border-orange-200"
                  >
                    {/* Area Image */}
                    <div className="relative h-40 overflow-hidden">
                      <img 
                        src={area.image} 
                        alt={area.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-900/50 to-transparent" />
                      <div className="absolute bottom-4 left-4">
                        <h3 className="text-white font-bold text-lg">{area.name}</h3>
                        <div className="flex items-center space-x-1 text-slate-200 text-sm">
                          <SafeIcon icon={FiMapPin} className="text-xs" />
                          <span>{area.region}</span>
                        </div>
                      </div>
                    </div>

                    {/* Card Content */}
                    <div className="p-6">
                      {/* Population */}
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-2 text-slate-600 text-sm">
                          <SafeIcon icon={FiUsers} className="text-xs" />
                          <span>{area.population}</span>
                        </div>
                        <span className="text-orange-600 text-sm font-medium">
                          {area.clients.length} clients
                        </span>
                      </div>

                      {/* Clients Preview */}
                      <div className="mb-4">
                        <div className="text-sm font-medium text-slate-700 mb-2">Local Clients:</div>
                        <div className="space-y-1">
                          {area.clients.slice(0, 3).map((client, clientIndex) => (
                            <div key={clientIndex} className="text-sm text-slate-600 flex items-center space-x-2">
                              <div className="w-1.5 h-1.5 bg-orange-500 rounded-full" />
                              <span>{client}</span>
                            </div>
                          ))}
                          {area.clients.length > 3 && (
                            <div className="text-sm text-orange-600 font-medium">
                              +{area.clients.length - 3} more clients
                            </div>
                          )}
                        </div>
                      </div>

                      {/* CTA */}
                      <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                        <span className="text-orange-600 font-medium group-hover:text-orange-700">
                          View Details
                        </span>
                        <SafeIcon 
                          icon={FiArrowRight} 
                          className="text-orange-600 group-hover:translate-x-1 transition-transform duration-300" 
                        />
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Don't See Your Area Listed?
            </h2>
            <p className="text-xl text-slate-300 mb-8">
              We work with businesses across the entire UK. Get in touch to discuss how we can help your local business succeed online.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="bg-orange-500 text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-orange-600 transition-all duration-300 inline-flex items-center justify-center space-x-3"
              >
                <span>Start Your Project</span>
                <SafeIcon icon={FiArrowRight} />
              </Link>
              {/* <button className="border border-slate-600 text-slate-300 px-8 py-4 rounded-full font-semibold hover:bg-slate-800 transition-colors duration-300">
                Call: 07769061291
              </button> */}
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ServiceAreas;